/**
 * Date formatting utilities for OctoTrack
 *
 * Centralizes all date formatting logic using date-fns.
 * Provides consistent date display across the application.
 *
 * @module lib/date-utils
 */

import {
  format,
  formatDistanceToNow,
  formatDistance,
  parseISO,
  isValid,
  isPast,
  isFuture,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  addDays,
} from "date-fns"

// ============================================================================
// Types
// ============================================================================

/**
 * Date input type - accepts Date, string (ISO), or null/undefined
 */
export type DateInput = Date | string | null | undefined

/**
 * Date format presets available for use
 */
export type DateFormatPreset =
  | "short" // "Jan 15, 2025"
  | "long" // "January 15, 2025"
  | "shortWithTime" // "Jan 15, 2025 at 14:30"
  | "longWithTime" // "January 15, 2025 at 14:30"
  | "time" // "14:30"
  | "timeWithSeconds" // "14:30:45"
  | "iso" // "2025-01-15"
  | "relative" // "2 hours ago"
  | "eta" // "Jan 15, 2025" or "—" if null

// ============================================================================
// Core Parsing Utilities
// ============================================================================

/**
 * Safely parse a date input into a Date object
 * Handles ISO strings, Date objects, and null/undefined
 *
 * @param input - Date string (ISO format), Date object, or null/undefined
 * @returns Date object or null if invalid
 *
 * @example
 * parseDate("2025-01-15T14:30:00Z") // Date object
 * parseDate(new Date()) // Date object
 * parseDate(null) // null
 * parseDate("invalid") // null
 */
export function parseDate(input: DateInput): Date | null {
  if (!input) return null

  try {
    if (input instanceof Date) {
      return isValid(input) ? input : null
    }

    if (typeof input === "string") {
      // Handle ISO strings
      const parsed = parseISO(input)
      return isValid(parsed) ? parsed : null
    }

    return null
  } catch {
    return null
  }
}

/**
 * Check if a date input is valid
 *
 * @param input - Date input to validate
 * @returns true if the input can be parsed to a valid date
 */
export function isValidDate(input: DateInput): boolean {
  return parseDate(input) !== null
}

// ============================================================================
// Format Functions
// ============================================================================

/**
 * Format a date using a preset format
 *
 * @param input - Date input (Date, ISO string, or null)
 * @param preset - Format preset to use
 * @param fallback - Fallback string if date is invalid (default: "—")
 * @returns Formatted date string or fallback
 *
 * @example
 * formatDate("2025-01-15T14:30:00Z", "short") // "Jan 15, 2025"
 * formatDate("2025-01-15T14:30:00Z", "shortWithTime") // "Jan 15, 2025 at 14:30"
 * formatDate(null, "short") // "—"
 */
export function formatDate(
  input: DateInput,
  preset: DateFormatPreset = "short",
  fallback: string = "—"
): string {
  const date = parseDate(input)
  if (!date) return fallback

  switch (preset) {
    case "short":
      return format(date, "MMM d, yyyy")
    case "long":
      return format(date, "MMMM d, yyyy")
    case "shortWithTime":
      return format(date, "MMM d, yyyy 'at' HH:mm")
    case "longWithTime":
      return format(date, "MMMM d, yyyy 'at' HH:mm")
    case "time":
      return format(date, "HH:mm")
    case "timeWithSeconds":
      return format(date, "HH:mm:ss")
    case "iso":
      return format(date, "yyyy-MM-dd")
    case "relative":
      return formatRelative(input)
    case "eta":
      return format(date, "MMM dd, yyyy")
    default:
      return format(date, "MMM d, yyyy")
  }
}

/**
 * Format a date with a custom pattern
 *
 * @param input - Date input
 * @param pattern - date-fns format pattern
 * @param fallback - Fallback string if date is invalid
 * @returns Formatted date string or fallback
 *
 * @example
 * formatDateCustom("2025-01-15", "EEEE, MMMM do") // "Wednesday, January 15th"
 */
export function formatDateCustom(
  input: DateInput,
  pattern: string,
  fallback: string = "—"
): string {
  const date = parseDate(input)
  if (!date) return fallback

  try {
    return format(date, pattern)
  } catch {
    return fallback
  }
}

// ============================================================================
// Shorthand Format Functions
// ============================================================================

/**
 * Format date as short date (Jan 15, 2025)
 */
export function formatShortDate(input: DateInput, fallback: string = "—"): string {
  return formatDate(input, "short", fallback)
}

/**
 * Format date as long date (January 15, 2025)
 */
export function formatLongDate(input: DateInput, fallback: string = "—"): string {
  return formatDate(input, "long", fallback)
}

/**
 * Format date with time (Jan 15, 2025 at 14:30)
 */
export function formatDateTime(input: DateInput, fallback: string = "—"): string {
  return formatDate(input, "shortWithTime", fallback)
}

/**
 * Format time only (14:30)
 */
export function formatTime(input: DateInput, fallback: string = "—"): string {
  return formatDate(input, "time", fallback)
}

/**
 * Format as ISO date (2025-01-15)
 */
export function formatISODate(input: DateInput, fallback: string = "—"): string {
  return formatDate(input, "iso", fallback)
}

/**
 * Format ETA date with null handling (Jan 15, 2025 or "—")
 * Uses slightly different format (dd instead of d) for consistency with ETAs
 */
export function formatEta(input: DateInput, fallback: string = "—"): string {
  return formatDate(input, "eta", fallback)
}

// ============================================================================
// Relative Time Functions
// ============================================================================

/**
 * Format date as relative time (e.g., "2 hours ago", "in 3 days")
 *
 * @param input - Date input
 * @param options - Options for formatting
 * @returns Relative time string or fallback
 *
 * @example
 * formatRelative("2025-01-15T12:00:00Z") // "2 hours ago" or "in 3 days"
 */
export function formatRelative(
  input: DateInput,
  options: { addSuffix?: boolean; fallback?: string } = {}
): string {
  const { addSuffix = true, fallback = "—" } = options
  const date = parseDate(input)
  if (!date) return fallback

  try {
    return formatDistanceToNow(date, { addSuffix })
  } catch {
    return fallback
  }
}

/**
 * Format the distance between two dates
 *
 * @param startDate - Start date
 * @param endDate - End date
 * @param options - Options for formatting
 * @returns Distance string or fallback
 *
 * @example
 * formatDateDistance("2025-01-10", "2025-01-15") // "5 days"
 */
export function formatDateDistance(
  startDate: DateInput,
  endDate: DateInput,
  options: { addSuffix?: boolean; fallback?: string } = {}
): string {
  const { addSuffix = false, fallback = "—" } = options
  const start = parseDate(startDate)
  const end = parseDate(endDate)

  if (!start || !end) return fallback

  try {
    return formatDistance(start, end, { addSuffix })
  } catch {
    return fallback
  }
}

// ============================================================================
// Date Comparison Utilities
// ============================================================================

/**
 * Check if a date is in the past
 */
export function isDatePast(input: DateInput): boolean {
  const date = parseDate(input)
  return date ? isPast(date) : false
}

/**
 * Check if a date is in the future
 */
export function isDateFuture(input: DateInput): boolean {
  const date = parseDate(input)
  return date ? isFuture(date) : false
}

/**
 * Get the difference in days between a date and now
 * Positive = future, Negative = past
 */
export function getDaysFromNow(input: DateInput): number | null {
  const date = parseDate(input)
  if (!date) return null
  return differenceInDays(date, new Date())
}

/**
 * Get the difference in hours between a date and now
 * Positive = future, Negative = past
 */
export function getHoursFromNow(input: DateInput): number | null {
  const date = parseDate(input)
  if (!date) return null
  return differenceInHours(date, new Date())
}

// ============================================================================
// Shipping/Logistics Specific Utilities
// ============================================================================

/**
 * Format an event date with time for timeline display
 * Returns both formatted date and time separately
 *
 * @param input - Date input
 * @returns Object with date, time, and relative strings
 *
 * @example
 * formatEventDate("2025-01-15T14:30:00Z")
 * // { date: "Jan 15, 2025", time: "14:30", relative: "2 hours ago", datetime: "Jan 15, 2025 at 14:30" }
 */
export function formatEventDate(input: DateInput): {
  date: string
  time: string
  relative: string
  datetime: string
} | null {
  const date = parseDate(input)
  if (!date) return null

  return {
    date: format(date, "MMM d, yyyy"),
    time: format(date, "HH:mm"),
    relative: formatDistanceToNow(date, { addSuffix: true }),
    datetime: format(date, "MMM d, yyyy 'at' HH:mm"),
  }
}

/**
 * Get ETA status information for display
 *
 * @param etaDate - Expected arrival date
 * @returns Status object with days until ETA, label, and urgency level
 *
 * @example
 * getEtaStatus("2025-01-20")
 * // { daysUntil: 5, label: "in 5 days", isOverdue: false, urgency: "normal" }
 */
export function getEtaStatus(etaDate: DateInput): {
  daysUntil: number | null
  label: string
  isOverdue: boolean
  urgency: "overdue" | "urgent" | "soon" | "normal"
} {
  const date = parseDate(etaDate)

  if (!date) {
    return {
      daysUntil: null,
      label: "No ETA",
      isOverdue: false,
      urgency: "normal",
    }
  }

  const daysUntil = differenceInDays(date, new Date())
  const isOverdue = daysUntil < 0

  let urgency: "overdue" | "urgent" | "soon" | "normal" = "normal"
  let label: string

  if (isOverdue) {
    urgency = "overdue"
    label = `${Math.abs(daysUntil)} day${Math.abs(daysUntil) === 1 ? "" : "s"} overdue`
  } else if (daysUntil === 0) {
    urgency = "urgent"
    label = "Today"
  } else if (daysUntil === 1) {
    urgency = "urgent"
    label = "Tomorrow"
  } else if (daysUntil <= 3) {
    urgency = "soon"
    label = `in ${daysUntil} days`
  } else {
    label = `in ${daysUntil} days`
  }

  return { daysUntil, label, isOverdue, urgency }
}

/**
 * Format a date range (e.g., for voyage display)
 *
 * @param startDate - Start date
 * @param endDate - End date
 * @param options - Formatting options
 * @returns Formatted date range string
 *
 * @example
 * formatDateRange("2025-01-10", "2025-01-20")
 * // "Jan 10 - Jan 20, 2025"
 */
export function formatDateRange(
  startDate: DateInput,
  endDate: DateInput,
  options: { separator?: string; fallback?: string } = {}
): string {
  const { separator = " - ", fallback = "—" } = options
  const start = parseDate(startDate)
  const end = parseDate(endDate)

  if (!start && !end) return fallback
  if (start && !end) return formatShortDate(start)
  if (!start && end) return formatShortDate(end)
  if (!start || !end) return fallback

  // Same year - omit year from start date
  if (start.getFullYear() === end.getFullYear()) {
    // Same month - show "Jan 10 - 20, 2025"
    if (start.getMonth() === end.getMonth()) {
      return `${format(start, "MMM d")}${separator}${format(end, "d, yyyy")}`
    }
    // Different months - show "Jan 10 - Feb 20, 2025"
    return `${format(start, "MMM d")}${separator}${format(end, "MMM d, yyyy")}`
  }

  // Different years - show full dates
  return `${formatShortDate(start)}${separator}${formatShortDate(end)}`
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Add days to a date
 */
export function addDaysToDate(input: DateInput, days: number): Date | null {
  const date = parseDate(input)
  if (!date) return null
  return addDays(date, days)
}

/**
 * Get the current date/time formatted
 */
export function getCurrentDateTime(preset: DateFormatPreset = "shortWithTime"): string {
  return formatDate(new Date(), preset)
}

/**
 * Check if two dates are on the same day
 */
export function isSameDay(date1: DateInput, date2: DateInput): boolean {
  const d1 = parseDate(date1)
  const d2 = parseDate(date2)
  if (!d1 || !d2) return false

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  )
}

// ============================================================================
// Data Freshness Utilities
// ============================================================================

/**
 * Format a timestamp as "Last updated X ago" for display
 * Provides user-friendly "last updated" text with relative time
 *
 * @param timestamp - The last updated timestamp
 * @param options - Formatting options
 * @returns Formatted "Last updated" string
 *
 * @example
 * formatLastUpdated("2025-01-23T14:55:00Z") // "Last updated 5 minutes ago"
 * formatLastUpdated("2025-01-23T10:00:00Z") // "Last updated 4 hours ago"
 * formatLastUpdated(null) // "Never updated"
 * formatLastUpdated(null, { fallback: "Not synced" }) // "Not synced"
 */
export function formatLastUpdated(
  timestamp: DateInput,
  options: { prefix?: string; fallback?: string } = {}
): string {
  const { prefix = "Last updated", fallback = "Never updated" } = options
  const date = parseDate(timestamp)

  if (!date) return fallback

  try {
    const relative = formatDistanceToNow(date, { addSuffix: true })
    return `${prefix} ${relative}`
  } catch {
    return fallback
  }
}

/**
 * Get time elapsed since update
 * Returns an object with time in hours, minutes, and a human-readable label
 *
 * @param timestamp - The last updated timestamp
 * @returns Object with hours, minutes, and label, or null if invalid
 *
 * @example
 * getTimeSinceUpdate("2025-01-23T14:30:00Z")
 * // { hours: 0, minutes: 30, label: "30 minutes ago" }
 *
 * getTimeSinceUpdate("2025-01-23T10:00:00Z")
 * // { hours: 4, minutes: 240, label: "4 hours ago" }
 */
export function getTimeSinceUpdate(timestamp: DateInput): {
  hours: number
  minutes: number
  label: string
} | null {
  const date = parseDate(timestamp)
  if (!date) return null

  try {
    const now = new Date()
    const hours = differenceInHours(now, date)
    const minutes = differenceInMinutes(now, date)
    const label = formatDistanceToNow(date, { addSuffix: true })

    return {
      hours,
      minutes,
      label,
    }
  } catch {
    return null
  }
}

/**
 * Check if data is stale based on a timestamp threshold
 * Data is considered stale if it hasn't been updated within the threshold
 *
 * @param timestamp - The last updated timestamp
 * @param thresholdHours - Hours threshold to consider data stale (default: 24)
 * @returns true if data is stale (older than threshold), false otherwise
 *
 * @example
 * isDataStale("2025-01-22T10:00:00Z", 24) // true if >24 hours ago
 * isDataStale("2025-01-23T14:00:00Z", 24) // false if <24 hours ago
 * isDataStale(null, 24) // false (no timestamp = not considered stale)
 */
export function isDataStale(
  timestamp: DateInput,
  thresholdHours: number = 24
): boolean {
  const date = parseDate(timestamp)
  if (!date) return false

  try {
    const hoursAgo = differenceInHours(new Date(), date)
    return hoursAgo > thresholdHours
  } catch {
    return false
  }
}
